import { useEffect, useRef } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useGetQRASavedStateQuery } from 'app/services/api/qra/savedstates'
import { openNewAssessmentCompletedSnackbar } from 'app/entities/store/snackbarSlice'
import { WISAssessmentStatusCode } from 'features/RiskManagement/Assessments/WISAssessmentStatusCode'
import { setSavedStateViewInfoSync } from 'features/RiskManagement/Assessments/States/qraSavedStateSlice'
import { selectSavedStateViewInfo } from 'features/RiskManagement/Assessments/States/qraSavedStateSelectors'

export const useSavedStateAssessmentStatusEffect = (
  stateId: string | undefined,
  assessmentId: string | undefined
) => {
  const dispatch = useAppDispatch()

  const savedStateInfo = useAppSelector(selectSavedStateViewInfo)

  const { state, refetch } = useGetQRASavedStateQuery(stateId ?? skipToken, {
    selectFromResult: (result) => ({
      state: result.currentData
    })
  })

  const timeoutRef = useRef<ReturnType<typeof setInterval>>()

  useEffect(() => {
    if (state?.assessments == null) {
      return
    }

    // REVIEW: track possible statusCode value in an Enum?
    const assessmentInProcess = state.assessments.find((assessment) => {
      const isProcessing = [
        WISAssessmentStatusCode.New,
        WISAssessmentStatusCode.DataPreparation,
        WISAssessmentStatusCode.InProcess,
        WISAssessmentStatusCode.RunningModels,
        WISAssessmentStatusCode.PostProcessing,
        WISAssessmentStatusCode.PopulatingToDB,
        WISAssessmentStatusCode.PopulatingToMap
      ].includes(assessment.statusCode)

      return isProcessing
    })

    if (savedStateInfo?.stateId !== state.id) {
      // Uninitialized or user switched from a different saved state

      dispatch(
        setSavedStateViewInfoSync({
          stateId: state.id,
          inProcessAssessmentId: assessmentInProcess?.id
        })
      )
    } else if (
      assessmentInProcess != null &&
      savedStateInfo.inProcessAssessmentId == null
    ) {
      // REVIEW: Detect new WIS run from saved states page?
      //         otherwise this may not be needed.

      dispatch(
        setSavedStateViewInfoSync({
          ...savedStateInfo,
          inProcessAssessmentId: assessmentInProcess.id
        })
      )
    } else if (
      assessmentInProcess == null &&
      savedStateInfo.inProcessAssessmentId != null
    ) {
      // Assessment has either completed or errored..
      const assessmentInQuestion = state.assessments.find(
        (assessment) => assessment.id === savedStateInfo.inProcessAssessmentId
      )

      if (assessmentInQuestion != null) {
        dispatch(
          setSavedStateViewInfoSync({
            ...savedStateInfo,
            inProcessAssessmentId: undefined,
            lastInProcessAssessmentId: assessmentInQuestion.id
          })
        )

        dispatch(
          openNewAssessmentCompletedSnackbar({
            id: '',
            savedStateName: state.name,
            currentAssessment: assessmentInQuestion
          })
        )
      }
    }

    if (assessmentInProcess != null) {
      timeoutRef.current = setInterval(() => {
        refetch().catch(() => {
          clearInterval(timeoutRef.current)
        })
      }, 22222)
    }

    return () => {
      clearInterval(timeoutRef.current)
    }
  }, [
    dispatch,
    refetch,
    assessmentId,
    savedStateInfo,
    state?.assessments,
    state?.id,
    state?.name
  ])
}
